import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../utils/supabase';
import Icon from '../../../components/AppIcon';

/**
 * AchievementsTab Component
 * Shows all achievements across all games for the user
 */
export default function AchievementsTab() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalXP: 0,
    level: 0,
    totalAchievements: 0,
    unlockedAchievements: 0,
    rareAchievements: 0
  });
  const [recentUnlocks, setRecentUnlocks] = useState([]);
  const [achievementsByGame, setAchievementsByGame] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user?.id) {
      loadAchievementData();
    }
  }, [user?.id]);

  const loadAchievementData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get user XP stats
      const { data: xpData, error: xpError } = await supabase
        .from('user_xp')
        .select('total_xp, level')
        .eq('user_id', user.id)
        .single();

      if (xpError && xpError.code !== 'PGRST116') throw xpError;

      // Get all unlocked achievements
      const { data: unlocksData, error: unlocksError } = await supabase
        .from('user_achievements')
        .select(`
          id,
          unlocked_at,
          achievement:achievement_id (
            id,
            name,
            description,
            icon_url,
            xp_reward,
            rarity,
            game:game_id (
              id,
              title,
              cover_image_url
            )
          )
        `)
        .eq('user_id', user.id)
        .order('unlocked_at', { ascending: false });

      if (unlocksError) throw unlocksError;

      // Get total achievements count
      const { count: totalCount, error: countError } = await supabase
        .from('achievements')
        .select('id', { count: 'exact', head: true });

      if (countError) throw countError;

      // Calculate stats
      const unlockedCount = unlocksData?.length || 0;
      const rareCount = unlocksData?.filter(u =>
        u.achievement?.rarity === 'rare' ||
        u.achievement?.rarity === 'epic' ||
        u.achievement?.rarity === 'legendary'
      ).length || 0;

      setStats({
        totalXP: xpData?.total_xp || 0,
        level: xpData?.level || 1,
        totalAchievements: totalCount || 0,
        unlockedAchievements: unlockedCount,
        rareAchievements: rareCount
      });

      // Recent unlocks (last 10)
      setRecentUnlocks(unlocksData?.slice(0, 10) || []);

      // Group by game
      const gameMap = {};
      (unlocksData || []).forEach(unlock => {
        const game = unlock.achievement?.game;
        if (game) {
          if (!gameMap[game.id]) {
            gameMap[game.id] = {
              game: game,
              achievements: []
            };
          }
          gameMap[game.id].achievements.push(unlock);
        }
      });

      setAchievementsByGame(Object.values(gameMap));

    } catch (err) {
      console.error('Error loading achievements:', err);
      setError('Failed to load achievement data');
    } finally {
      setLoading(false);
    }
  };

  const getXPForNextLevel = (currentLevel) => {
    // XP formula: level = floor(sqrt(totalXP / 100))
    // Solve for totalXP: totalXP = (level + 1)^2 * 100
    return Math.pow(currentLevel + 1, 2) * 100;
  };

  const getRarityColor = (rarity) => {
    switch (rarity) {
      case 'uncommon':
        return 'text-green-500';
      case 'rare':
        return 'text-blue-500';
      case 'epic':
        return 'text-purple-500';
      case 'legendary':
        return 'text-yellow-500';
      default:
        return 'text-gray-500';
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  const nextLevelXP = getXPForNextLevel(stats.level);
  const progressToNextLevel = ((stats.totalXP % (nextLevelXP - Math.pow(stats.level, 2) * 100)) / (nextLevelXP - Math.pow(stats.level, 2) * 100)) * 100;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
          <p className="text-sm text-muted-foreground">Loading achievements...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Icon name="AlertCircle" size={48} className="text-red-500 mx-auto mb-4" />
          <p className="text-red-500">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* XP Progress Card */}
      <div className="bg-gradient-to-br from-primary/10 to-purple-500/10 border border-primary/20 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-2xl font-bold text-foreground">Level {stats.level}</h3>
            <p className="text-sm text-muted-foreground">{stats.totalXP.toLocaleString()} Total XP</p>
          </div>
          <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center">
            <Icon name="Zap" size={32} className="text-primary" />
          </div>
        </div>

        {/* Progress to next level */}
        <div className="mb-2">
          <div className="flex justify-between text-sm text-muted-foreground mb-1">
            <span>Progress to Level {stats.level + 1}</span>
            <span>{Math.round(progressToNextLevel)}%</span>
          </div>
          <div className="w-full bg-muted rounded-full h-3">
            <div
              className="bg-gradient-to-r from-primary to-purple-500 h-3 rounded-full transition-all duration-500"
              style={{ width: `${progressToNextLevel}%` }}
            />
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          {(nextLevelXP - stats.totalXP).toLocaleString()} XP needed for next level
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Award" size={24} className="text-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{stats.unlockedAchievements}</p>
              <p className="text-sm text-muted-foreground">Achievements Unlocked</p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center">
              <Icon name="Star" size={24} className="text-purple-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{stats.rareAchievements}</p>
              <p className="text-sm text-muted-foreground">Rare+ Achievements</p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center">
              <Icon name="Target" size={24} className="text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">
                {stats.totalAchievements > 0
                  ? Math.round((stats.unlockedAchievements / stats.totalAchievements) * 100)
                  : 0}%
              </p>
              <p className="text-sm text-muted-foreground">Completion Rate</p>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Unlocks */}
      <div className="bg-card border border-border rounded-lg">
        <div className="p-4 border-b border-border">
          <h4 className="font-semibold text-foreground flex items-center gap-2">
            <Icon name="Clock" size={18} />
            Recent Unlocks
          </h4>
        </div>

        {recentUnlocks.length === 0 ? (
          <div className="p-8 text-center">
            <Icon name="Award" size={64} className="text-muted-foreground mx-auto mb-4 opacity-50" />
            <p className="text-muted-foreground">No achievements unlocked yet</p>
            <p className="text-sm text-muted-foreground mt-2">Start playing games to earn achievements!</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {recentUnlocks.map(unlock => (
              <div key={unlock.id} className="p-4 flex items-center gap-4 hover:bg-muted/50 transition-colors">
                {/* Icon */}
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  {unlock.achievement?.icon_url ? (
                    <img
                      src={unlock.achievement.icon_url}
                      alt={unlock.achievement.name}
                      className="w-8 h-8 object-contain"
                    />
                  ) : (
                    <Icon name="Award" size={24} className="text-primary" />
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h5 className="font-semibold text-foreground truncate">
                      {unlock.achievement?.name}
                    </h5>
                    {unlock.achievement?.rarity && unlock.achievement.rarity !== 'common' && (
                      <span className={`text-xs font-semibold uppercase ${getRarityColor(unlock.achievement.rarity)}`}>
                        {unlock.achievement.rarity}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground truncate">
                    {unlock.achievement?.game?.title}
                  </p>
                </div>

                {/* XP and time */}
                <div className="text-right flex-shrink-0">
                  {unlock.achievement?.xp_reward && (
                    <div className="text-sm font-semibold text-primary flex items-center gap-1 justify-end mb-1">
                      <Icon name="Zap" size={14} />
                      +{unlock.achievement.xp_reward} XP
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">{formatTimeAgo(unlock.unlocked_at)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Achievements by Game */}
      {achievementsByGame.length > 0 && (
        <div className="bg-card border border-border rounded-lg">
          <div className="p-4 border-b border-border">
            <h4 className="font-semibold text-foreground flex items-center gap-2">
              <Icon name="Gamepad2" size={18} />
              Achievements by Game
            </h4>
          </div>

          <div className="divide-y divide-border">
            {achievementsByGame.map(({ game, achievements }) => (
              <div key={game.id} className="p-4">
                <div className="flex items-center gap-3 mb-3">
                  <img
                    src={game.cover_image_url || '/placeholder-game.png'}
                    alt={game.title}
                    className="w-12 h-12 rounded object-cover"
                  />
                  <div className="flex-1">
                    <h5 className="font-semibold text-foreground">{game.title}</h5>
                    <p className="text-sm text-muted-foreground">
                      {achievements.length} achievement{achievements.length !== 1 ? 's' : ''} unlocked
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                  {achievements.slice(0, 8).map(unlock => (
                    <div
                      key={unlock.id}
                      className="flex items-center gap-2 bg-muted/50 rounded p-2"
                      title={unlock.achievement?.name}
                    >
                      <div className="w-8 h-8 bg-primary/10 rounded flex items-center justify-center flex-shrink-0">
                        {unlock.achievement?.icon_url ? (
                          <img
                            src={unlock.achievement.icon_url}
                            alt={unlock.achievement.name}
                            className="w-6 h-6 object-contain"
                          />
                        ) : (
                          <Icon name="Award" size={16} className="text-primary" />
                        )}
                      </div>
                      <span className="text-xs text-foreground truncate flex-1">
                        {unlock.achievement?.name}
                      </span>
                    </div>
                  ))}
                  {achievements.length > 8 && (
                    <div className="flex items-center justify-center bg-muted/50 rounded p-2 text-xs text-muted-foreground">
                      +{achievements.length - 8} more
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
